import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
}

const SEO = ({
  title,
  description,
  keywords,
  ogImage = '/logo.png',
  ogUrl,
  canonical,
  jsonLd,
  author = 'AIlicorn Inc.',
  publishedDate,
  modifiedDate,
}: SEOProps) => {
  const fullTitle = `${title} | AIlicorn`;
  const jsonLdEntries = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // Add default Organization schema
  const defaultOrgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'AIlicorn',
    url: 'https://www.ailicorn.com',
    logo: 'https://www.ailicorn.com/logo.png',
    description: 'Leading AI fall detection and sensing technology provider',
    sameAs: ['https://www.linkedin.com/company/ailicorn-inc'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'service@ailicorn.com',
      availableLanguage: ['en', 'zh-TW'],
    },
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Language */}
      <meta httpEquiv="Content-Language" content="en, zh-TW" />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {ogImage && (
        <>
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={`${fullTitle} - ${description.substring(0, 100)}`} />
        </>
      )}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="AIlicorn" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="zh_TW" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && (
        <>
          <meta name="twitter:image" content={ogImage} />
          <meta name="twitter:image:alt" content={`${fullTitle}`} />
        </>
      )}
      <meta name="twitter:site" content="@ailicorn" />
      <meta name="twitter:creator" content="@ailicorn" />
      
      {/* Mobile App */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="AIlicorn" />
      <meta name="theme-color" content="#5B8FC6" />
      <meta name="msapplication-navbutton-color" content="#5B8FC6" />
      
      {/* Canonical */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Robots */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Article Metadata */}
      {publishedDate && <meta property="article:published_time" content={publishedDate} />}
      {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      
      {/* Performance Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(defaultOrgSchema)}
      </script>
      
      {jsonLdEntries.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
